import { type ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { RiskLevel } from "@/lovable-demo/lib/warranty/types";
import { statusLabel } from "@/lovable-demo/lib/warranty/calc";

export function Panel({
  title,
  subtitle,
  actions,
  children,
  className,
}: {
  title?: string | undefined;
  subtitle?: string | undefined;
  actions?: ReactNode | undefined;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <section
      className={cn("min-w-0 rounded-lg border border-border bg-card panel-glow", className)}
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0 flex-1">
            {title && (
              <h2 className="text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {title}
              </h2>
            )}
            {subtitle && <p className="mt-1 text-sm text-foreground/80">{subtitle}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className="min-w-0 p-4">{children}</div>
    </section>
  );
}

const riskStyles: Record<RiskLevel, string> = {
  critical: "border-risk-critical/40 bg-risk-critical/15 text-risk-critical",
  high: "border-risk-high/40 bg-risk-high/15 text-risk-high",
  medium: "border-risk-medium/40 bg-risk-medium/15 text-risk-medium",
  low: "border-risk-low/40 bg-risk-low/15 text-risk-low",
};

export function RiskBadge({ risk, score }: { risk: RiskLevel; score?: number | undefined }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-[0.68rem] font-semibold uppercase tracking-wider",
        riskStyles[risk],
      )}
    >
      <span className="size-1.5 rounded-full bg-current" />
      {risk}
      {score !== undefined && <span className="num opacity-80">{score}</span>}
    </span>
  );
}

export function StatusChip({ status }: { status: string }) {
  const tone =
    status === "closed"
      ? "border-success/40 bg-success/12 text-success"
      : status === "signal"
        ? "border-info/40 bg-info/12 text-info"
        : status === "verification"
          ? "border-primary/40 bg-primary/12 text-primary"
          : "border-warning/40 bg-warning/12 text-warning";
  return (
    <span
      className={cn(
        "inline-flex rounded border px-2 py-0.5 text-[0.68rem] font-medium uppercase tracking-wider",
        tone,
      )}
    >
      {statusLabel[status] ?? status}
    </span>
  );
}

export function VerdictBadge({ verdict }: { verdict: string }) {
  const tone =
    verdict === "Compliant" ||
    verdict === "Effective" ||
    verdict === "cleared" ||
    verdict === "verified" ||
    verdict === "recovered"
      ? "border-success/40 bg-success/12 text-success"
      : verdict === "Violated" || verdict === "Not Effective"
        ? "border-destructive/40 bg-destructive/15 text-destructive"
        : "border-warning/40 bg-warning/12 text-warning";
  return (
    <span className={cn("inline-flex rounded border px-2 py-0.5 text-xs font-medium", tone)}>
      {verdict}
    </span>
  );
}

export function KpiCard({
  label,
  value,
  delta,
  hint,
  tone = "default",
  chart,
}: {
  label: string;
  value: string;
  delta?: string | undefined;
  hint?: string | undefined;
  tone?: "default" | "good" | "bad" | "warn" | undefined;
  chart?: ReactNode | undefined;
}) {
  const toneClass =
    tone === "good"
      ? "text-success"
      : tone === "bad"
        ? "text-destructive"
        : tone === "warn"
          ? "text-warning"
          : "text-primary";
  return (
    <div className="rounded-lg border border-border bg-card p-4 panel-glow">
      <p
        className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
        title={
          label === "Remaining exposure" ? "Total exposure minus actual cost to date." : undefined
        }
      >
        {label}
      </p>
      <div className="mt-2 flex items-end justify-between gap-3">
        <p className={cn("num text-2xl font-semibold", toneClass)}>{value}</p>
        {chart && <div className="h-9 w-24 shrink-0">{chart}</div>}
      </div>
      {(delta || hint) && (
        <p className="mt-2 text-xs text-muted-foreground">
          {delta && <span className={cn("num font-medium", toneClass)}>{delta}</span>}
          {delta && hint && " · "}
          {hint}
        </p>
      )}
    </div>
  );
}

export function Field({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div>
      <p className="text-[0.66rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-sm text-foreground">{value}</p>
    </div>
  );
}

export function SectionTitle({
  children,
  hint,
}: {
  children: ReactNode;
  hint?: string | undefined;
}) {
  return (
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-foreground">{children}</h3>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
